import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Cpu } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Niches from "@/pages/Niches";
import ChannelKit from "@/pages/ChannelKit";
import Channels from "@/pages/Channels";
import Strategy from "@/pages/Strategy";
import Production from "@/pages/Production";
import Approvals from "@/pages/Approvals";
import Publishing from "@/pages/Publishing";
import Analytics from "@/pages/Analytics";
import Monetization from "@/pages/Monetization";
import Retention from "@/pages/Retention";
import Integrations from "@/pages/Integrations";
import Security from "@/pages/Security";
import System from "@/pages/System";
import Admin from "@/pages/Admin";

function LoadingScreen() {
    return (
        <div className="grain flex min-h-screen items-center justify-center bg-background text-foreground">
            <div className="flex h-12 w-12 animate-pulse items-center justify-center border border-primary text-primary">
                <Cpu size={22} />
            </div>
        </div>
    );
}

function AppRouter() {
    const location = useLocation();
    const { user, loading } = useAuth();

    // Process the OAuth callback FIRST (synchronous check on the reactive hash).
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    if (loading) return <LoadingScreen />;
    if (!user) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }
    return (
        <Layout>
            <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/niches" element={<Niches />} />
                <Route path="/kit" element={<ChannelKit />} />
                <Route path="/channels" element={<Channels />} />
                <Route path="/strategy" element={<Strategy />} />
                <Route path="/production" element={<Production />} />
                <Route path="/approvals" element={<Approvals />} />
                <Route path="/publishing" element={<Publishing />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/monetization" element={<Monetization />} />
                <Route path="/retention" element={<Retention />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/security" element={<Security />} />
                <Route path="/system" element={<System />} />
                {user.role === "admin" && <Route path="/admin" element={<Admin />} />}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function App() {
    return (
        <BrowserRouter>
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#050505",
                        border: "1px solid #262626",
                        borderRadius: 0,
                        fontFamily: "JetBrains Mono, monospace",
                        color: "#fff",
                    },
                }}
            />
            <AuthProvider>
                <AppRouter />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
